import { lodestarColors } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { useActive } from "@smartsheet/lodestar-utils";
import React, { CSSProperties, forwardRef, KeyboardEvent, KeyboardEventHandler, MouseEvent, ReactNode } from "react";
import { sizes } from "../../../constants/spacing";
import { useCombinedThemeMode } from "../../../util/theme/useCombinedThemeMode";
import { useOverflowTooltip } from "../../../util/useOverflowTooltip";
import { Tooltip, TooltipBody } from "../../tooltip";
import { ChipLabel } from "../components/ChipLabel";
import { ChipContainer } from "../styled/ChipContainer";
import { ChipRemoveButton } from "../styled/ChipRemoveButton";
import { chipCustomCssProperties, getTokenForColor } from "../styled/chipConstants";
import { ChipColorVariant, SemanticChipStatus } from "../types";
import { SharedChipProps } from "../types";

const elementIsScrollable = (elem: HTMLElement | null) => {
    return !!elem && elem.offsetHeight < elem.scrollHeight;
};

export interface BaseChipProps extends SharedChipProps {
    /**
     * The standard color to pick from.
     */
    color: ChipColorVariant | SemanticChipStatus;

    /**
     * Stuff that renders between the left (in LTR) side of the chip and the text.
     */
    contentBefore?: ReactNode;
}

export const BaseChip = forwardRef<HTMLDivElement, BaseChipProps>(
    (
        {
            "aria-label": ariaLabel,
            "aria-labelledby": ariaLabelledby,
            "aria-describedby": ariaDescribedBy,
            children,
            className,
            color: colorVariant = "neutral",
            onKeyDown,
            onRemove,
            clientId,
            isReadonly = false,
            contentBefore,
            omitTooltip = false,
            size = "medium",
            ...rest
        },
        ref
    ) => {
        const { isActive, ...handlers } = useActive<HTMLDivElement>();
        const isInteractive = !isReadonly;

        const handleRemove = (event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => {
            if (isInteractive) {
                onRemove?.(event);
            }
        };
        const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
            if (isInteractive) {
                onKeyDown?.(event);
                switch (event.key) {
                    case "Backspace":
                    case "Delete":
                        handleRemove(event);
                }
            }
        };

        const themeMode = useCombinedThemeMode();
        const buttonProps = onRemove
            ? {
                  role: "button",
                  onKeyDown: handleKeyDown,
                  tabIndex: 0,
              }
            : {};

        const { showOverflowTooltip, tooltipProps, targetProps, elementToMeasureRef } = useOverflowTooltip<
            HTMLSpanElement,
            HTMLDivElement
        >({
            tooltipTargetRef: ref,
            elementIsOverflowing: elementIsScrollable,
            onBlur: rest.onBlur,
            onFocus: rest.onFocus,
            onMouseEnter: rest.onMouseEnter,
            onMouseLeave: rest.onMouseLeave,
            isDisabled: omitTooltip,
        });

        const colorProperties = getTokenForColor(colorVariant);

        return (
            <ChipContainer
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledby}
                aria-describedby={ariaDescribedBy}
                aria-disabled={isReadonly || undefined}
                data-client-id={clientId}
                className={className}
                isReadonly={isReadonly}
                themeMode={themeMode}
                size={size}
                {...buttonProps}
                {...rest}
                {...targetProps}
                style={
                    {
                        ...rest.style,
                        // We'll apply these properties here so we don't have to generate as much
                        // CSS for the Chips. This is good for readability and performance.
                        [chipCustomCssProperties.text]: color(colorProperties.text, lodestarColors.neutralDark40),
                        [chipCustomCssProperties.backgroundRest]: color(
                            colorProperties.backgroundRest,
                            lodestarColors.buttonDefault
                        ),
                        [chipCustomCssProperties.backgroundHover]: color(
                            colorProperties.backgroundHover,
                            lodestarColors.buttonDefault
                        ),
                        [chipCustomCssProperties.backgroundActive]: color(
                            colorProperties.backgroundActive,
                            lodestarColors.buttonDefault
                        ),
                    } as CSSProperties
                }
            >
                {contentBefore}
                <ChipLabel
                    ref={elementToMeasureRef}
                    size={size}
                    // We're going to apply our own here.
                    omitTooltip
                >
                    {children}
                </ChipLabel>
                {!isReadonly && onRemove ? (
                    <ChipRemoveButton
                        aria-hidden
                        isReadonly={isReadonly}
                        {...handlers}
                        isActive={isActive}
                        onClick={handleRemove}
                    >
                        <svg
                            height={sizes.medium}
                            width={sizes.medium}
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M4.22876 4.22876L11.7712 11.7712"
                                stroke="currentColor"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M4.22888 11.7713L11.7714 4.22887"
                                stroke="currentColor"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </ChipRemoveButton>
                ) : null}
                {showOverflowTooltip && (
                    <Tooltip {...tooltipProps}>
                        <TooltipBody>{children}</TooltipBody>
                    </Tooltip>
                )}
            </ChipContainer>
        );
    }
);

BaseChip.displayName = "BaseChip";
